import { ITextFieldProps, SpinnerSize, Stack, TextField, Label, Icon } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { Control, Controller, FieldErrors, ValidationRule } from "react-hook-form";

import { useDelayedState } from "../../hooks/use-delayed-state/useDelayedState";
import { FormValidationRules } from "../../utils/types/FormValidationRules";
import { getValidate } from "../../utils/validation/getValidate";
import { FailureSeverity, ValidationFailure } from "../../utils/validation/ValidationResult";
import { TextFieldClearButton } from "../inputs/button/TextFieldClearButton";
import { Spinner } from "../progress/Spinner";
import { TooltipHost, TooltipHostProps } from "../surfaces/tooltip/TooltipHost";
import { FormErrorMessage } from "./FormErrorMessage";
import { FormFailureMessages } from "./FormFailureMessages";
import { getFormLabelText } from "./getFormLabelText";
import { useFormWatch } from "./useFormWatch";

export type FormTimeFieldProp = Pick<ITextFieldProps, "multiline" | "disabled"> & {
    name: string;
    label: string;
    placeholder?: string;
    control: Control;
    errors: FieldErrors;
    isLoading?: boolean;
    dependsOn?: string[];
    rules?: FormTimePickerRules;
    height?: number;
    value?: string;
    maxValue?: string;
    minValue?: string;
    tooltipContent?: TooltipHostProps["content"];
    onInputChange?(inputValue?: string): void;
    onBlur?(): void;
};

export const FormTimeField: React.FunctionComponent<FormTimeFieldProp> = ({
    name,
    label,
    placeholder,
    height,
    minValue,
    control,
    errors,
    isLoading,
    dependsOn,
    rules,
    value,
    tooltipContent,
    maxValue,
    disabled,
    onInputChange,
    onBlur
}: FormTimeFieldProp) => {
    const [warnings, setWarnings] = useState<ValidationFailure[]>([]);

    const clearWarnings = () => {
        setWarnings([]);
    };

    const handleOnChange = () => {
        if (onInputChange) {
            onInputChange("");
        }
        clearWarnings();
    };

    if (dependsOn) {
        useFormWatch({
            name,
            control,
            dependsOn,
            onClear: handleOnChange
        });
    }

    const render = (renderProp: { value: string | undefined; onChange(value?: string): void }) => {
        const [delayedValue, inputValue, setInputValue] = useDelayedState(value);

        useEffect(() => {
            renderProp.onChange(delayedValue);

            if (onInputChange) {
                onInputChange(delayedValue);
            }
        }, [delayedValue]);

        const clearButtonId = `${name}_TextFieldClearButtonId`;

        const clearValue = () => {
            renderProp.onChange(undefined);

            if (onInputChange) {
                onInputChange(undefined);
            }
        };

        const handleBlur = (event?: React.FocusEvent<HTMLElement>) => {
            const relatedTarget = event?.relatedTarget as EventTarget & HTMLElement;

            if (relatedTarget?.id !== clearButtonId && onBlur) {
                onBlur();
            }
        };

        const handleInputChange = (
            _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            newValue?: string
        ) => {
            clearWarnings();
            setInputValue(newValue);
        };

        const handleClearButtonClick = () => {
            clearValue();
            clearWarnings();
            handleBlur();
        };

        const renderClearButton = () => (
            <TextFieldClearButton disabled={!renderProp.value} onClick={handleClearButtonClick} id={clearButtonId} />
        );

        const renderSpinner = () => (
            <Stack verticalAlign="center" styles={{ root: { height: "100%" } }}>
                <Spinner size={SpinnerSize.small} />
            </Stack>
        );

        const renderSuffix = () => {
            return (
                <Stack verticalAlign="center" horizontal={true}>
                    {isLoading && renderSpinner()}
                    {!!renderProp.value && renderClearButton()}
                </Stack>
            );
        };

        const renderLabel = () => {
            return (
                <Stack>
                    <Stack horizontal={true} horizontalAlign="space-between" verticalAlign="center">
                        <Label disabled={disabled}>{getFormLabelText(label, !!rules?.required)}</Label>

                        {tooltipContent && (
                            <TooltipHost content={tooltipContent}>
                                <Stack>
                                    <Icon iconName="Info" styles={{ root: { marginLeft: 5, cursor: "default" } }} />
                                </Stack>
                            </TooltipHost>
                        )}
                    </Stack>
                </Stack>
            );
        };

        return (
            <Stack onBlur={handleBlur}>
                <TextField
                    data-testtype={"text"}
                    data-testid={name}
                    name={name}
                    placeholder={placeholder}
                    onRenderLabel={renderLabel}
                    styles={{
                        fieldGroup: { border: errors[name] && "2px solid red" },
                        suffix: { display: "none" },
                        root: { width: "100%" },
                        field: { height: height }
                    }}
                    onRenderSuffix={renderSuffix}
                    onChange={handleInputChange}
                    value={inputValue || ""}
                    disabled={disabled}
                    type="time"
                    step={"1"}
                    max={maxValue}
                    min={minValue}
                />
            </Stack>
        );
    };

    return (
        <Stack>
            <Controller
                name={name}
                control={control}
                rules={{
                    ...rules,
                    validate: getValidate<string, FormTimePickerRules>(
                        rules,
                        control.formStateRef.current.submitCount,
                        setWarnings
                    )
                }}
                render={render}
            />
            <FormErrorMessage name={name} errors={errors} />
            <FormFailureMessages failures={warnings} severity={FailureSeverity.Warning} />
        </Stack>
    );
};

export interface FormTimePickerRules extends FormValidationRules<string> {
    min?: string | number;
    pattern?: ValidationRule<RegExp>;
}
